import { useEffect, useRef, useState } from 'react';
import { useChat } from '../store';
import { api } from '../api';
import { dateHeading } from '../utils/time';
import { MessageBubble } from './MessageBubble';
import type { Message } from '../types';

interface MessageListProps {
  conversationId: string;
}

export function MessageList({ conversationId }: MessageListProps) {
  const me = useChat((s) => s.me);
  // Read raw so we can distinguish "not loaded yet" (undefined) from
  // "loaded and empty" (empty array). The skeleton state below uses this.
  const rawMessages = useChat((s) => s.messagesByConv[conversationId]);
  const messages = rawMessages ?? [];
  const hasLoaded = rawMessages !== undefined;
  const hasMore = useChat((s) => s.hasMoreByConv[conversationId]) ?? false;
  const conversation = useChat((s) =>
    s.conversations.find((c) => c.id === conversationId),
  );
  const prependMessages = useChat((s) => s.prependMessages);
  const jumpToMessageId = useChat((s) => s.jumpToMessageId);
  const setJumpToMessage = useChat((s) => s.setJumpToMessage);

  const containerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const [flashMessageId, setFlashMessageId] = useState<string | null>(null);
  // State (not just ref) so the floating jump-to-bottom button can re-render.
  // We mirror stickToBottomRef into state on each scroll event.
  const [nearBottom, setNearBottom] = useState(true);
  // Counts new arrivals while the user is scrolled up. Resets to 0 when
  // they jump back to the bottom or auto-stick re-engages.
  const [newSinceScrollUp, setNewSinceScrollUp] = useState(0);
  const lastSeenLengthRef = useRef(messages.length);

  // Auto-scroll to bottom on new messages, but only if the user is near the bottom.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      setNewSinceScrollUp(0);
    } else {
      // Scrolled up: bump the new-message badge by the count of newly
      // arrived messages since the last render (clamped at 99 visually,
      // but we keep the real number for badge text).
      const delta = messages.length - lastSeenLengthRef.current;
      if (delta > 0) setNewSinceScrollUp((prev) => prev + delta);
    }
    lastSeenLengthRef.current = messages.length;
  }, [messages.length, conversationId]);

  // Reset the new-message counter when switching conversations — the
  // counter is per-list, not per-conversation, so it would otherwise
  // bleed across switches.
  useEffect(() => {
    setNewSinceScrollUp(0);
    lastSeenLengthRef.current = messages.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Honor a pending jump request: scroll the target into view and flash
  // the bubble so the user can find it. If the target isn't in the
  // loaded slice (typical for a search hit on older history), we fetch
  // a window centered on it via the around=<id> endpoint and rebind
  // the slice; the same effect re-runs once the new messages arrive.
  const setMessages = useChat((s) => s.setMessages);
  useEffect(() => {
    if (!jumpToMessageId) return;
    if (!messages.some((m) => m.id === jumpToMessageId)) {
      // Not in the loaded slice — fetch a window around it. Don't clear
      // jumpToMessageId yet; the next render with the new slice will
      // hit the scroll branch below.
      let cancelled = false;
      void (async () => {
        try {
          const res = await api.messagesAround(conversationId, jumpToMessageId);
          if (cancelled) return;
          // Replace the slice. The MessageList's auto-stick effect would
          // normally yank to the bottom, but stickToBottomRef gets
          // flipped to false when we scroll the target into view in the
          // next iteration of this effect.
          setMessages(conversationId, res.messages, res.hasMore);
          stickToBottomRef.current = false;
        } catch {
          // Best-effort. Drop the jump so it doesn't loop.
          setJumpToMessage(null);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    const el = document.querySelector<HTMLElement>(
      `[data-message-id="${jumpToMessageId}"]`,
    );
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Pause auto-stick — the user just navigated, don't yank them back.
      stickToBottomRef.current = false;
      setFlashMessageId(jumpToMessageId);
      window.setTimeout(() => setFlashMessageId(null), 1600);
    }
    setJumpToMessage(null);
  }, [jumpToMessageId, messages, setJumpToMessage, conversationId, setMessages]);

  // Track scroll position to decide auto-stick. The state mirror is so
  // the floating button re-renders; we don't want to drop the ref-only
  // version because the auto-scroll effect reads it synchronously.
  function onScroll() {
    const el = containerRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNear = dist < 80;
    stickToBottomRef.current = isNear;
    setNearBottom((prev) => (prev !== isNear ? isNear : prev));
    if (isNear) setNewSinceScrollUp(0);
  }

  function jumpToBottom() {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setNewSinceScrollUp(0);
    stickToBottomRef.current = true;
    setNearBottom(true);
  }

  // Mark conversation read when newest visible message changes.
  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1]!;
    if (last.senderId !== me?.id && me) {
      void api.markRead(conversationId, last.id).catch(() => {});
    }
  }, [conversationId, me, messages]);

  async function loadMore() {
    if (!hasMore) return;
    const oldest = messages[0];
    if (!oldest) return;
    try {
      const res = await api.messages(conversationId, oldest.id);
      prependMessages(conversationId, res.messages, res.hasMore);
    } catch {
      /* swallow */
    }
  }

  if (!conversation) return null;

  return (
    <div className="flex-1 relative min-h-0">
      {/* Floating jump-to-bottom button. Only shows once the user is
          meaningfully scrolled up; reset to invisible when they're back
          near the bottom. New-message badge surfaces if any arrivals
          happened while they were scrolled up. */}
      {!nearBottom && (
        <button
          onClick={jumpToBottom}
          aria-label="Jump to latest"
          title="Jump to latest"
          className="absolute bottom-3 right-3 z-10 group flex items-center gap-1.5 glass-chrome hairline shadow-elevation rounded-full pl-2 pr-3 py-1.5 text-zinc-300 hover:text-zinc-50 transition-all duration-300 ease-spring active:scale-[0.94] animate-fade-up"
        >
          <span className="text-base leading-none">↓</span>
          <span className="text-[11px] font-medium">
            {newSinceScrollUp > 0
              ? `${newSinceScrollUp > 99 ? '99+' : newSinceScrollUp} new`
              : 'Latest'}
          </span>
        </button>
      )}
    <div
      ref={containerRef}
      onScroll={onScroll}
      className="absolute inset-0 overflow-y-auto py-3 chat-wallpaper"
    >
      {hasMore && messages.length > 0 && (
        <div className="flex justify-center py-2">
          <button
            onClick={loadMore}
            className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-1 rounded-md bg-zinc-900 border border-zinc-800 transition"
          >
            Load earlier messages
          </button>
        </div>
      )}

      {!hasLoaded && (
        // Skeleton bubbles while the initial fetch is in flight. Alternates
        // sides so it visually reads as a chat. Auto-clears as soon as
        // setMessages writes the first response into the store.
        <div className="px-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => {
            const mine = i % 2 === 1;
            const widths = ['w-1/2', 'w-2/5', 'w-3/5', 'w-1/3', 'w-1/2'];
            return (
              <div
                key={i}
                className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`h-9 ${widths[i % widths.length]} rounded-[1.25rem] bg-zinc-900 animate-pulse`}
                />
              </div>
            );
          })}
        </div>
      )}

      {hasLoaded && messages.length === 0 && (
        <div className="flex-1 flex items-center justify-center py-12">
          <p className="text-sm text-zinc-500">No messages yet — say hi.</p>
        </div>
      )}

      {messages.map((m, i) => {
        const prev = i > 0 ? messages[i - 1]! : null;
        const isMine = m.senderId === me?.id;
        const sender = conversation.participants.find((p) => p.id === m.senderId);

        // Show sender name if previous message was from a different sender.
        const showSender =
          conversation.type !== 'dm' && (!prev || prev.senderId !== m.senderId);

        // Show date heading if the calendar day differs from the previous message.
        const showDate = !prev || sameDayDiffers(prev.createdAt, m.createdAt);

        return (
          <div
            key={m.id}
            data-message-id={m.id}
            className={
              flashMessageId === m.id
                ? 'rounded-2xl ring-2 ring-amber-400/70 transition-all duration-700'
                : 'transition-all duration-700'
            }
          >
            {showDate && (
              // Sticky so the date pill stays parked at the top of the
              // viewport as the user scrolls through that day's messages.
              // When the next day's pill approaches, it pushes the
              // current one out — standard CSS sticky chaining.
              // top-1 keeps a hairline of space against the chrome above.
              <div className="sticky top-1 z-[5] flex justify-center my-3 pointer-events-none">
                <span className="text-[10px] uppercase tracking-wider text-zinc-300 px-2 py-0.5 bg-zinc-900/90 hairline rounded-full backdrop-blur pointer-events-auto">
                  {dateHeading(m.createdAt)}
                </span>
              </div>
            )}
            <MessageBubble
              message={m}
              showSender={showSender}
              senderName={sender?.displayName ?? sender?.email ?? null}
              isMine={isMine}
            />
          </div>
        );
      })}
    </div>
    </div>
  );
}

function sameDayDiffers(a: string, b: string): boolean {
  return new Date(a).toDateString() !== new Date(b).toDateString();
}

// Avoid unused-import warning: Message is referenced via useChat selectors only.
export type { Message };
